import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import Grow from '@base/Grow/Grow';
import P2 from '@base/P2/P2';
import H6 from '@base/H6/H6';
import Link from '@base/Link/Link';
import Tags from '@base/Tags/Tags';
import { getDateShort, dateWithTime } from '@utils/dates';
import isEmptyObject from '@utils/isEmptyObject';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import {
  SEARCH_RESULT_CLICKED,
  searchObjectTypes,
  TEST_SERIES_SECTION_CLICKED,
  SEARCH_RESULT_PAGE
} from '@constants/segment';
import { getCommaSeparatedUIDs } from '@utils/textHelper';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { Spacings, SOLID_BORDER_STYLE } from '@styles/index';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getMeUid } from '@api/selectors/auth';

const Live = styled(Tags)`
  margin-top: ${Spacings.SPACING_3B};
  @media (max-width: 47.95em) {
    margin-top: 16px;
  }
`;

const Title = styled(H6)`
  @media (max-width: 47.95em) {
    font-size: 16px;
  }
`;

const getInfoString = (test) => {
  const { nextTrack, isCompleted } = test;
  if (isCompleted) return `Ended on ${getDateShort(test.endsAt)}`;
  if (!nextTrack || isEmptyObject(nextTrack)) return null;
  return `${nextTrack.type === 'quiz' ? 'Test' : 'Lesson'} ${
    nextTrack.contentRank || nextTrack.rank
  } • ${dateWithTime(
    nextTrack.type === 'quiz'
      ? nextTrack.startsAt || nextTrack.startTime
      : nextTrack.liveAt || nextTrack.liveTime
  )}`;
};

const renderBottom = (test) => {
  const { isCompleted } = test;
  return (
    <>
      <Grow />
      <P2
        color={
          isCompleted
            ? 'var(--color-text-secondary)'
            : 'var(--color-text-primary)'
        }
      >
        {getInfoString(test)}
      </P2>
    </>
  );
};

const TestSeriesCard = ({
  test,
  gridColumn,
  linkStyle,
  dataAnalytics,
  orientation,
  index,
  type,
  updateRecentSearchLog,
  searchData,
  className
}) => {
  const dispatch = useDispatch();
  const goalSegmentData = useGoalEventProps({});
  const meUID = useMyInfo(getMeUid);
  const { name, isLive, slug, uid, colorCode, isSpecial, authors } = test;
  const asPath = `/test-series/${slug}/${uid}`;
  const styledLink = `background-color: var(--color-base-1);
  grid-column: ${gridColumn};
  padding: ${Spacings.SPACING_4B};
  border-radius: ${Spacings.SPACING_2B};
  border-top: ${Spacings.SPACING_2B} ${SOLID_BORDER_STYLE} ${colorCode};
  cursor: pointer;
  display: flex;
  flex: 1;
  height: 170px;
  flex-direction:${orientation === 'vertical' ? 'column' : 'row'} ;
  align-items: flex-start;
  box-shadow: ${boxShadowGenerator()};
  :hover {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_2B
    })};
  }
  transition: box-shadow 0.2s ease-in-out;`;

  const handleAnalytics = () => {
    const testData = test || {};
    const testUID = testData.uid || '';

    segment.track(TEST_SERIES_SECTION_CLICKED, {
      ...goalSegmentData,
      index,
      object_type: searchObjectTypes.testSeries,
      object_id: testUID,
      is_plus: testData.forPlus,
      is_special: testData.isSpecial,
      educator_uid: getCommaSeparatedUIDs(authors),
      educator_username: getCommaSeparatedUIDs(authors, 'username'),
      educator_name: getCommaSeparatedUIDs(authors, 'name')
    });

    if (type === 'search') {
      const nSearchData = { ...searchData };
      delete nSearchData.query;
      const position = nSearchData.position ? nSearchData.position : 'See all';
      segment.track(
        SEARCH_RESULT_CLICKED,
        {
          ...goalSegmentData,
          sub_position: index + 1,
          is_object_plus: testData.forPlus,
          is__object_special: testData.isSpecial,
          educator_uid: getCommaSeparatedUIDs(authors),
          educator_username: getCommaSeparatedUIDs(authors, 'username'),
          educator_name: getCommaSeparatedUIDs(authors, 'name'),
          last_primary_source: SEARCH_RESULT_PAGE,
          last_primary_source_section: 'Test-series',
          search_section: 'Global',
          current_screen: 'Test-series',
          eventName: SEARCH_RESULT_CLICKED,
          eventType: 'click',
          version: 2,
          objectID: `${searchData.entity_type}:${testUID}`,
          userToken: meUID,
          ...nSearchData,
          position
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
      dispatch(
        updateRecentSearchLog({
          ...searchData,
          entity_id: `programme:${testUID}`,
          goal_uid: test.goalUid || test.goal.uid
        })
      );
    }
  };

  return (
    <Link
      as={asPath}
      href="/test-series/[slug]/[courseUID]"
      anchorStyle={styledLink + linkStyle}
      onClick={handleAnalytics}
      className={className}
    >
      <Title
        color="var(--color-text-primary)"
        lineClamp={2}
        data-analytics={`${dataAnalytics}-courseCard`}
        as="h4"
      >
        {name}
      </Title>
      {isLive ? <Live type="live">LIVE</Live> : null}
      {!isLive && isSpecial ? <Live type="live">FREE</Live> : null}
      {renderBottom(test)}
    </Link>
  );
};

TestSeriesCard.propTypes = {
  test: PropTypes.objectOf(PropTypes.any).isRequired,
  gridColumn: PropTypes.string,
  linkStyle: PropTypes.string,
  dataAnalytics: PropTypes.string,
  index: PropTypes.number.isRequired,
  orientation: PropTypes.string,
  type: PropTypes.string,
  updateRecentSearchLog: PropTypes.func,
  searchData: PropTypes.objectOf(PropTypes.any),
  className: PropTypes.string
};

TestSeriesCard.defaultProps = {
  gridColumn: 'span 6',
  linkStyle: '',
  dataAnalytics: '',
  orientation: 'vertical',
  type: '',
  updateRecentSearchLog: () => {},
  searchData: {},
  className: ''
};

export default TestSeriesCard;
